import { hasLiveStoredOidcSession } from './ensureValidOidcSession'

export const RENEW_RETRY_BASE_MS = 5_000
export const RENEW_RETRY_MAX_MS = 5 * 60_000

// Credential verdicts, where a retry can only get the same answer. svc-auth
// spells this `invalid_client`; the other RFC 6749 codes are here so we don't
// depend on that. `invalid_request` is excluded deliberately: it describes the
// shape of the request, not the validity of the credential.
export const TERMINAL_RENEW_ERRORS = new Set([
  'access_denied',
  'invalid_client',
  'invalid_grant',
  'unauthorized_client',
])

// svc-auth's 429 body also carries an `error`, so the class alone means nothing;
// oidc-client-ts sets `name` as a cross-bundle marker, avoiding a static import.
const isOAuthErrorResponse = (error: unknown): error is { error: string | null } =>
  error instanceof Error && error.name === 'ErrorResponse'

export function isTerminalAuthError(error: unknown): boolean {
  if (isOAuthErrorResponse(error)) {
    return TERMINAL_RENEW_ERRORS.has(error.error ?? '')
  }

  const status = (error as { status?: number } | undefined)?.status

  return status === 401 || status === 403
}

export interface SilentRenewer {
  dispose(): void
  renew(): void
}

interface SilentRenewerOptions {
  onSessionEnded: () => void | Promise<void>
  onTransientError: (error: unknown) => void
  /** Must serialise across tabs; a bare signinSilent here reintroduces the race. */
  renew: () => Promise<string | undefined>
}

export function createSilentRenewer(options: SilentRenewerOptions): SilentRenewer {
  const { renew: runRenew, onSessionEnded, onTransientError } = options

  let failures = 0
  let retryHandle: ReturnType<typeof setTimeout> | undefined
  let isDisposed = false
  let isRenewing = false

  const scheduleRetry = () => {
    const delay = Math.min(RENEW_RETRY_BASE_MS * 2 ** (failures - 1), RENEW_RETRY_MAX_MS)
    // Jitter so tabs recovering from a svc-auth blip don't resynchronise.
    const jittered = delay * (0.5 + Math.random() / 2)

    if (retryHandle) {
      clearTimeout(retryHandle)
    }

    retryHandle = setTimeout(() => {
      retryHandle = undefined

      if (!isDisposed) {
        renew()
      }
    }, jittered)
  }

  const attemptRenew = async() => {
    try {
      await runRenew()
      failures = 0
    } catch(error) {
      // A sign-out redirect may already be under way.
      if (isDisposed) {
        return
      }

      if (isTerminalAuthError(error)) {
        // A token another tab rotated away is refused identically to a dead one,
        // so a live stored session means that tab won and this one is done.
        if (await hasLiveStoredOidcSession()) {
          failures = 0

          return
        }

        failures = 0
        await onSessionEnded()

        return
      }

      // Offline, 5xx, rate-limited: giving up would delete a working refresh
      // token over a passing network blip.
      failures += 1
      onTransientError(error)
      scheduleRetry()
    }
  }

  function renew(): void {
    // The coordinator registers this on three triggers (expiring, expired,
    // foreground), which overlap around an expiry. Without this each overlapping
    // call would count the same failure again, inflating the backoff and
    // re-reporting one error N times.
    if (isDisposed || isRenewing) {
      return
    }

    isRenewing = true

    // An unhandled rejection here would leave no retry scheduled and no armed
    // timer, killing renewal silently for the rest of the session.
    attemptRenew().catch((error) => {
      if (isDisposed) {
        return
      }

      failures += 1
      onTransientError(error)
      scheduleRetry()
    }).finally(() => {
      isRenewing = false
    })
  }

  return {
    renew,
    dispose() {
      isDisposed = true

      if (retryHandle) {
        clearTimeout(retryHandle)
        retryHandle = undefined
      }
    },
  }
}
